import type { ChangeEvent, KeyboardEvent, RefObject } from "react";
import type { TooltipPosition, TooltipTrigger } from "./Tooltips";

/**
 * Interface representing a checkbox component.
 */
export interface ICheckbox {
  // Basic properties

  /**
   * Unique identifier for the checkbox.
   */
  id: string;

  /**
   * Indicates whether the checkbox is checked.
   */
  checked: boolean;

  /**
   * Optional flag to disable the checkbox.
   */
  disabled?: boolean;

  // Styling properties

  /**
   * Optional class name for the checkbox.
   */
  className?: string;

  // Event handlers

  /**
   * Function to call when the checkbox state changes.
   * @param e - The change event of the checkbox.
   */
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

// Define possible types for an input
export enum InputType {
  Number = "number",
  TextArea = "textarea",
  Text = "text",
  Date = "date",
  Dropdown = "dropdown",
  DropdownWithColour = "dropdown-with-colour",
  Email = "email",
  Time = "time",
  Toggle = "toggle",
  Password = "password",
  Combobox = "combobox",
  Image = "image",
  CheckButtons = "checkButtons",
}

export interface IBaseInput<InputElement> {
  // Basic properties
  id?: string;
  value: string | number;
  label?: string | number;
  disabled?: boolean;
  description?: string;
  placeholder?: string;

  // Styling properties
  wrapperClassName?: string;
  labelClassName?: string;
  inputClassName?: string;

  // Event handlers
  onChange?: (e: ChangeEvent<InputElement>) => void;
  onKeyDown?: (e: KeyboardEvent<InputElement>) => void;

  // Miscellaneous properties
  customRef?: RefObject<InputElement | null>;
  tabIndex?: number;
  isDefaultValue?: boolean;

  // Tooltip properties
  showTooltip?: boolean;
  tooltip?: {
    labels: {
      title: string;
      description: string;
      contentWidth: string;
      buttonText: string;
    };

    /**
     * The trigger for the tooltip.
     * Possible values are "click", "hover", or "focus".
     * @default TooltipTrigger.Hover
     */
    trigger: TooltipTrigger;
    /**
     * The position of the tooltip.
     * Possible values are "top", "right", "bottom", or "left".
     * @default TooltipPosition.Top
     */
    position: TooltipPosition;
    buttonOnClick?: () => void;
    interactive: true;
    showButton: boolean;
  };
}

/**
 * Interface representing an input component.
 * Extends the IBaseInput interface with HTMLInputElement as the generic type.
 */
export interface IInput extends IBaseInput<HTMLInputElement> {
  // Basic properties

  /**
   * Optional type of the input.
   * Possible values are "number", "textarea", "text", "date", "dropdown", "email", "time", or "toggle".
   * @default InputType.Text
   */
  inputType?: InputType;

  // Styling properties

  /**
   * Optional background color of the input.
   */
  inputBackground?: string;

  /**
   * Optional text color of the input.
   */
  colour?: string;

  // Validation properties

  /**
   * Optional pattern that the input's value must match.
   */
  pattern?: string;

  /**
   * Optional maximum length of the input's value.
   */
  maxLength?: number;

  /**
   * Optional minimum length of the input's value.
   */
  minLength?: number;

  /**
   * Optional minimum value for numeric inputs.
   */
  min?: number;

  /**
   * Optional maximum value for numeric inputs.
   */
  max?: number;
}

/**
 * Interface representing a limited input component.
 * Extends the IInput interface, omitting the 'value' and 'onChange' properties.
 */
export interface ILimitedInput extends Omit<IInput, "value" | "onChange"> {
  // Basic properties

  /**
   * The current value of the input.
   */
  value: string;

  // Validation properties

  /**
   * The maximum number of characters allowed in the input.
   */
  limit: number;

  // Event handlers

  /**
   * Function to call when the input value changes.
   * @param value - The new value of the input.
   */
  onChange: (value: string) => void;

  // Miscellaneous properties

  /**
   * Optional flag to show the character count.
   */
  showCount?: boolean;
}

// Text Area Input
export interface ITextArea extends IBaseInput<HTMLTextAreaElement> {}

/**
 * Interface representing a limited text area component.
 * Extends the ITextArea interface, omitting the 'value' and 'onChange' properties.
 */
export interface ILimitedTextArea
  extends Omit<ITextArea, "value" | "onChange"> {
  // Basic properties

  /**
   * The current value of the text area.
   */
  value: string;

  // Validation properties

  /**
   * The maximum number of characters allowed in the text area.
   */
  limit: number;

  // Event handlers

  /**
   * Function to call when the text area value changes.
   * @param value - The new value of the text area.
   */
  onChange: (value: string) => void;

  // Miscellaneous properties

  /**
   * Optional flag to show the character count.
   */
  showCount?: boolean;
}

export enum IToggleLabelPosition {
  Leading = "leading",
  Trailing = "trailing",
}

/**
 * Interface representing the properties of a toggle component.
 */
export interface IToggle {
  /**
   * Indicates whether the toggle is enabled.
   */
  enabled: boolean;

  /**
   * Function to call when the toggle state changes.
   * @param e - The new state of the toggle.
   */
  onChange: (e: boolean) => void;

  /**
   * Optional colour of the toggle.
   */
  colour?: string;

  /**
   * Optional flag to disable the toggle.
   */
  disabled?: boolean;

  /**
   * Optional label for the toggle.
   */
  label?: string;

  /**
   * Unique identifier for the toggle.
   */
  id: string;

  /**
   * Optional class name for the wrapper element.
   */
  wrapperClassName?: string;

  /**
   * Optional class name for the label element.
   */
  labelClassName?: string;

  /**
   * Optional Position of the label element.
   * Possible values are "leading" or "trailing"
   * @default IToggleLabelPosition.Leading
   */
  labelPosition?: IToggleLabelPosition;
}

export interface IRadioButton {
  /**
   * Unique identifier for the radio button.
   */
  id: string;

  /**
   * Name of the radio button group.
   */
  name: string;

  /**
   * Indicates whether the radio button is checked.
   */
  checked: boolean;

  /**
   * Optional flag to disable the radio button.
   */
  disabled?: boolean;

  /**
   * Function to call when the radio button state changes.
   * @param e - The change event of the radio button.
   */
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

export interface IDefaultDropdown {
  id: string;
  label?: string;
  value: string | number;
  onChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  options: {
    id: string | number;
    label: string | number;
    value: string | number;
    disabled?: boolean;
  }[];
  disabled?: boolean;
}

/**
 * Interface representing a dropdown component with color selection and optional tooltip.
 * @interface IDefaultDropdownWithColour
 * @extends IDefaultDropdown
 * @property {string} selectedColour - The selected color for the dropdown.
 * @property {boolean} [showTooltip] - Optional flag to show the tooltip.
 * @property {object} [tooltip] - Optional tooltip configuration.
 * @property {object} tooltip.labels - Labels for the tooltip.
 * @property {string} tooltip.labels.title - The title of the tooltip.
 * @property {string} tooltip.labels.description - The description of the tooltip.
 * @property {string} tooltip.labels.contentWidth - The width of the tooltip content.
 * @property {string} tooltip.labels.buttonText - The text for the tooltip button.
 * @property {TooltipTrigger} tooltip.trigger - The trigger for the tooltip.
 * @property {TooltipPosition} tooltip.position - The position of the tooltip.
 * @property {function} [tooltip.buttonOnClick] - Optional function to call when the tooltip button is clicked.
 * @property {boolean} tooltip.interactive - Indicates if the tooltip is interactive.
 * @property {boolean} tooltip.showButton - Indicates if the tooltip should show a button.
 */
export interface IDefaultDropdownWithColour extends IDefaultDropdown {
  selectedColour: string;
  showTooltip?: boolean;
  tooltip?: {
    labels: {
      title: string;
      description: string;
      contentWidth?: string;
      buttonText?: string;
    };

    trigger: TooltipTrigger;
    position: TooltipPosition;
    buttonOnClick?: () => void;
    interactive: boolean;
    showButton: boolean;
  };
}
